import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import compact from 'lodash/compact';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';

import { SelectCompanyThreeDStockProductsModalButtonRequiredProps } from './SelectCompanyThreeDStockProductsModalButton.types';
import { ErrorMessage, I18nText } from '../../../types';
import { FetchProductsSetByUserIdScopeType } from '../../../main/productsSets/productsSetsTypes';
import { TeamNanoID } from '../../../main/teams/teamsTypes';
import { FetchProductsFilters } from '../../../main/products/productsTypes';

import {
  FETCH_TEAM_USERS_QUERY,
  FetchTeamUsersQueryResponse
} from '../../../main/teams/queries/fetchTeamUsers.query';

import { useModal } from '../../modals/hooks/useModal';
import { useCacheTeam } from '../../../main/teams/hooks/useCacheTeam';
import { useProductsFiltersCache } from '../../../main/products/hooks/useProductsFiltersCache';
import { useCurrentUser } from '../../../auth/hooks/useAuth';

import { SimpleModal } from '../../modals/SimpleModal';

import {
  ThreeDStockOnProductsSelect,
  ThreeDStockProducts
} from '../../../main/stock/pages/ThreeDStockProducts';
import { PureTooltipIconButtonHelper } from '../../buttons/PureTooltipIconButtonHelper';

import { ProductCache } from '../../../main/products/ProductCache';
import { ProductsSetCache } from '../../../main/productsSets/ProductsSetCache';
import { ProductsPermissions } from '../../../main/products/productsConstants';
import { TeamCache } from '../../../main/teams/TeamCache';

const SELECT_COMPANY_3D_STOCK_PRODUCTS_BUTTON =
  'select-company-3d-stock-products-button';

interface SelectCompanyThreeDStockProductsModalButtonBaseProps {
  value?: string[];
  disabled?: boolean;
  onModalClose?: () => void;
  submitErrorMessage?: ErrorMessage;
  productsSetScope?: FetchProductsSetByUserIdScopeType;
  shouldOpen?: boolean;
  companyNanoId: TeamNanoID;
}

interface SelectCompanyThreeDStockProductsModalButtonWithTextProps {
  buttonText: string;
  i18nText?: never;
}

interface SelectCompanyThreeDStockProductsModalButtonWithI18nTextProps {
  buttonText?: never;
  i18nText: I18nText;
}

interface SelectCompanyThreeDStockProductsModalButtonWithOnSubmitProps {
  onSubmit: (value: string[]) => Promise<unknown>;
  onChange?: never;
}

interface SelectCompanyThreeDStockProductsModalButtonWithOnChangeProps {
  onSubmit?: never;
  onChange: (value: string[]) => void;
}

type SelectCompanyThreeDStockProductsModalButtonProps =
  SelectCompanyThreeDStockProductsModalButtonBaseProps &
    (
      | SelectCompanyThreeDStockProductsModalButtonWithTextProps
      | SelectCompanyThreeDStockProductsModalButtonWithI18nTextProps
    ) &
    (
      | SelectCompanyThreeDStockProductsModalButtonWithOnChangeProps
      | SelectCompanyThreeDStockProductsModalButtonWithOnSubmitProps
    );

function SelectCompanyThreeDStockProductsModalButton({
  companyNanoId,
  value = [],
  disabled,
  onModalClose,
  onChange,
  onSubmit,
  className,
  i18nTitle,
  icon,
  iconClassName,
  modalIcon,
  i18nSubmitText,
  tooltipPlacement,
  tooltipI18nText,
  i18nText,
  buttonText,
  shouldOpen,
  withoutInitialFocus,
  productsSetScope
}: SelectCompanyThreeDStockProductsModalButtonProps &
  SelectCompanyThreeDStockProductsModalButtonRequiredProps) {
  const currentUser = useCurrentUser();
  const { team } = useCacheTeam<FetchTeamUsersQueryResponse>({
    cacheKey: TeamCache.showTeamUsersCacheKey(),
    query: FETCH_TEAM_USERS_QUERY,
    uuid: companyNanoId,
    options: {
      enabled: !!companyNanoId,
      enabledPlaceholder: false
    }
  });

  const clientIds = map(team?.users, 'id');
  const noClients = isEmpty(clientIds);

  const selectedProductIds = useRef<string[]>([]);
  const attachProductIds = useRef<string[]>(value);
  const [productCategoryFilters, setProductCategoryFilters] =
    useState<FetchProductsFilters>({});

  const initialFilters = {
    ...(currentUser.hasPermissions(ProductsPermissions.READ_PRODUCTS_WITH_TEAMS)
      ? { teamIds: [team?.uuid] }
      : { clientIds }),
    blocked: false,
    productCategoryId: productCategoryFilters?.productCategoryId,
    productParentCategoryIds: productCategoryFilters?.productParentCategoryIds
  };

  const { resetCachedProductsFiltersAndSort } = useProductsFiltersCache({
    cacheKey: ProductCache.companyLibraryCacheKey(companyNanoId),
    initialFilters
  });

  const handleOpen = useCallback<() => void>(() => {
    attachProductIds.current = value;
    selectedProductIds.current = [];
    resetCachedProductsFiltersAndSort();
  }, [resetCachedProductsFiltersAndSort, value]);

  const handleClose = useCallback<() => void>(() => {
    const selectedProductsIds = filter(attachProductIds.current, (productId) =>
      selectedProductIds.current.includes(productId)
    );

    if (onChange) {
      onChange(selectedProductsIds);
    } else {
      return onSubmit?.(selectedProductsIds);
    }
  }, [onChange, onSubmit]);

  const handleModalClose = useCallback<() => void>(() => {
    handleClose();
    onModalClose?.();
  }, [handleClose, onModalClose]);

  const { isOpen, showModal, hideModal } = useModal({
    onOpen: handleOpen,
    onClose: handleModalClose
  });

  useEffect(() => {
    if (shouldOpen && !isOpen && !disabled && !noClients) {
      showModal();
    }
  }, [shouldOpen, isOpen, disabled, noClients, showModal]);

  const handleProductsSelect = useCallback<ThreeDStockOnProductsSelect>(
    (selectedProducts) => {
      selectedProductIds.current = selectedProducts.map(
        (selectedProduct) => selectedProduct?.id
      );
    },
    []
  );

  const handleProductsAttach = useCallback<ThreeDStockOnProductsSelect>(
    (attachProducts) => {
      const selectedProductsIds = compact(
        attachProducts.map((selectedProduct) => selectedProduct.id)
      );

      attachProductIds.current = selectedProductsIds;

      hideModal();
    },
    [hideModal]
  );

  return (
    <Fragment>
      {buttonText ? (
        <PureTooltipIconButtonHelper
          className={className}
          disabled={disabled || noClients}
          icon={icon}
          iconClassName={iconClassName}
          tooltipPlacement={tooltipPlacement}
          tooltipI18nText={tooltipI18nText}
          text={buttonText}
          onClick={showModal}
          dataGa={SELECT_COMPANY_3D_STOCK_PRODUCTS_BUTTON}
        />
      ) : (
        <PureTooltipIconButtonHelper
          className={className}
          disabled={disabled || noClients}
          icon={icon}
          iconClassName={iconClassName}
          tooltipPlacement={tooltipPlacement}
          tooltipI18nText={tooltipI18nText}
          i18nText={i18nText}
          onClick={showModal}
          dataGa={SELECT_COMPANY_3D_STOCK_PRODUCTS_BUTTON}
        />
      )}

      <SimpleModal
        isOpen={isOpen}
        hideModal={hideModal}
        childrenClassName="flex-1 flex flex-col overflow-hidden"
        i18nTitle={i18nTitle}
        modalIcon={modalIcon}
        modalSize="full"
        i18nSubmitText={i18nSubmitText}
        disabled={disabled || noClients}
        isLoading={disabled}
        withoutInitialFocus={withoutInitialFocus}
        withoutButtons
      >
        <ThreeDStockProducts
          initialLimit={48}
          initialFilters={initialFilters}
          onProductsAttach={handleProductsAttach}
          onProductsSelect={handleProductsSelect}
          productsSetScope={productsSetScope}
          productsCacheKey={ProductCache.companyLibraryCacheKey(companyNanoId)}
          productsSetCacheKey={ProductsSetCache.showCompanyLibraryCacheKey(
            companyNanoId
          )}
          companyNanoId={companyNanoId}
          companyUuid={team?.uuid}
          onChangeFilters={setProductCategoryFilters}
          isModal
        />
      </SimpleModal>
    </Fragment>
  );
}

export default SelectCompanyThreeDStockProductsModalButton;
